import { MentionUser } from 'api/clients/MentionClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { ReactComponent as Doc } from 'assets/icons/doc24.svg'
import { ReactComponent as NewDoc } from 'assets/icons/documentAdd16.svg'
import { ReactComponent as FolderIcon } from 'assets/icons/folder24.svg'
import { ReactComponent as NewFolder } from 'assets/icons/folderAdd16.svg'
import cx from 'classnames'
import Avatar from 'common/Avatar/Avatar'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { ArticleFileIcon } from 'components/ArticleFileIcon/ArticleFileIcon'
import { stringType } from 'components/Handbook2_0/util'
import { useKeyboardNavigation } from 'hooks/useKeyboardNavigationHook'
import { useDebounce } from 'hooks/useDebounce'
import useOpenWindowSafely from 'hooks/useOpenWindowSafely'
import { capitalize } from 'lodash'
import isEqual from 'lodash/isEqual'
import { SelectionAttributes, TRIGGER_CHAR } from 'proseShared/plugins/MentionPrompt/MentionPrompt'
import { TextSelection } from 'prosemirror-state'
import { type EditorView } from 'prosemirror-view'
import { useAlgoliaClient } from 'providers/AlgoliaClientProvider'
import qs from 'qs'
import * as React from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { ArticleFlat, HandbookItemType, MentionableArticle, RootState } from 'types'
import { createNewArticle, getInitials } from 'utils'
import { getAlmanacUrlFor, getArticlePath, getUserProfilePath } from 'utils/urlUtils'
import { useArticles } from '../queries/useArticles'
import { useUsers } from '../queries/useUsers'
import Styles from './MentionPrompt.module.scss'

/**
 * Projects are currently not supported alone
 * and must be queried with Articles.
 *
 * @example
 * [ObjectsToLoad.Articles, ObjectsToLoad.Projects] // => valid
 * [ObjectsToLoad.Projects] // => invalid
 */
export enum ObjectsToLoad {
  Users = 'users',
  Articles = 'articles',
  Projects = 'projects',
}

interface Props {
  inputQuery?: string // NOTE: if not present a input will be shown
  handbookItemType?: HandbookItemType
  onCancel?: () => void
  onSelect: (attrs: SelectionAttributes) => void
  mentioningArticle?: ArticleFlat
  parentFolderId?: number
  suggestRecentlyReadArticle?: boolean
  allowCreateNewFile?: boolean
  createNewDocPos?: 'top' | 'bottom'
  dropdownWidth?: string
  objectsToLoad?: ObjectsToLoad[]
  onCreateStart?: () => void
  onCreateEnd?: (article?: ArticleFlat) => Promise<void>
  overrideContainerClassname?: string
  resultsListClassName?: string
  editableDocsOnly?: boolean
  extraOptions?: [React.FunctionComponent<React.PropsWithChildren<any>>, Object | undefined][]
  extraOptionsPosition?: 'top' | 'bottom'
  onExtraOptionSelect?: (selectedIndex: number) => void
  onKeyDown?: (event: KeyboardEvent) => void
  onBlur?: (event: React.FocusEvent<HTMLElement>) => void
  skipCreate?: boolean
  availableForReview?: boolean
  selectFirstItem?: boolean
  allowMentioning?: boolean
  title?: string
  showTitleWithNoResults?: boolean
}

const DebounceInterval = 250
export const DefaultItems = 3

const ResultStub = ({ index }: { index: number }) => (
  <div className={cx(Styles.resultStub, Styles[`resultStub-${index}`])}>
    <div className={Styles.resultStubEmoji} />
    <div>
      <div className={Styles.resultStubTitle} />
      <div className={Styles.resultStubMetadata} />
    </div>
  </div>
)

export const MentionPrompt = ({
  inputQuery,
  handbookItemType,
  onCancel,
  onSelect,
  mentioningArticle,
  onCreateStart,
  onCreateEnd,
  allowCreateNewFile = true,
  parentFolderId,
  createNewDocPos = 'bottom',
  suggestRecentlyReadArticle = true,
  dropdownWidth = '360px',
  objectsToLoad = [ObjectsToLoad.Users, ObjectsToLoad.Articles, ObjectsToLoad.Projects],
  overrideContainerClassname,
  resultsListClassName,
  editableDocsOnly = false,
  extraOptions,
  extraOptionsPosition = 'bottom',
  onKeyDown = (e: KeyboardEvent) => {},
  onBlur = (e) => {},
  skipCreate,
  availableForReview = false,
  selectFirstItem = true,
  allowMentioning = true,
  title = 'Add a mention',
  showTitleWithNoResults = true,
}: Props) => {
  const auth = useSelector((state: RootState) => state.auth, isEqual)
  const location = useLocation()
  const params = qs.parse(location.search, { ignoreQueryPrefix: true })
  const userId = params.userId
  const openWindowSafely = useOpenWindowSafely()
  const canCreateNewFile = allowCreateNewFile && !userId

  const loadOnlyUsers = objectsToLoad.includes(ObjectsToLoad.Users) && !objectsToLoad.includes(ObjectsToLoad.Articles)
  const skipLoadingUsers = !objectsToLoad.includes(ObjectsToLoad.Users)

  const algoliaClient = useAlgoliaClient()

  const originalQuery = inputQuery || ''
  const query = allowMentioning ? originalQuery : ''
  const queryIsEmpty = useMemo(() => !originalQuery.trim().length, [originalQuery])
  const creatingDoc = useRef(false)
  const [firstItemSelected, setFirstItemSelected] = useState<boolean>(selectFirstItem)
  const debouncedSearchTerm = useDebounce(query, DebounceInterval)
  let renderIndex = 0
  const resetFirstItemSelected = () => setFirstItemSelected(false)

  const selectArticle = useCallback(
    ({ title, url, key, header_emoji, last_edited_at, last_edited_by }: MentionableArticle) =>
      onSelect({
        type: 'Article',
        url: url,
        title: title,
        key: key,
        header_emoji: header_emoji || undefined,
        last_edited_at: last_edited_at,
        last_edited_by: last_edited_by,
      }),
    [onSelect],
  )
  const selectFolder = useCallback(
    ({ title, url, key, header_emoji, last_edited_at, last_edited_by }: MentionableArticle) =>
      onSelect({
        type: 'Folder',
        url: url,
        title: title,
        key: key,
        header_emoji: header_emoji || undefined,
        last_edited_at: last_edited_at,
        last_edited_by: last_edited_by,
      }),
    [onSelect],
  )

  const selectUser = useCallback(
    ({ display_name, id }: MentionUser) =>
      onSelect({
        type: 'User',
        title: display_name,
        url: getAlmanacUrlFor(getUserProfilePath({ displayName: display_name, id })),
        userId: `${id}`,
      }),
    [onSelect],
  )

  const selectNewlyCreatedDoc = useCallback(
    ({ title, url, key }: { title: string; url: string; key: string }) => {
      onSelect({
        type: 'Article',
        url: url,
        title: title,
        key: key,
      })
    },
    [onSelect],
  )

  const articlesAndFoldersQuery = useArticles({
    algoliaClient,
    auth,
    userId,
    editableDocsOnly,
    mentioningArticle,
    objectsToLoad,
    query: debouncedSearchTerm,
    suggestRecentlyReadArticle,
    availableForReview,
  })

  const usersQuery = useUsers({ auth, query: debouncedSearchTerm, objectsToLoad })

  const totalResults = useMemo(() => {
    return (articlesAndFoldersQuery.data?.length || 0) + (usersQuery.data?.length || 0)
  }, [usersQuery.data, articlesAndFoldersQuery.data])

  const createDocFromInputQuery = useCallback(
    async (inputQuery: string, auth: any) => {
      if (!canCreateNewFile || (!mentioningArticle && !parentFolderId) || creatingDoc.current) {
        return
      }

      creatingDoc.current = true
      onCreateStart?.()

      if (!skipCreate) {
        const dismissLoading = showToastMessage('Creating document...', 'loading', undefined, 0)
        const response = await createNewArticle({
          workspaceKey: auth.company_key,
          projectId: parentFolderId || mentioningArticle?.project_id,
          title: inputQuery,
        })
        dismissLoading()
        if (response.type === SUCCESS_RESPONSE_TYPE) {
          if (onCreateEnd) {
            onCreateEnd?.(response.data.article).finally(() => (creatingDoc.current = false))
          } else {
            const slug: string = getArticlePath({ slug: response.data.article.slug })
            openWindowSafely(slug)
            selectNewlyCreatedDoc({ title: inputQuery, url: slug, key: response.data.article.id })
            creatingDoc.current = false
          }
        } else {
          showErrorMessageToast('Something went wrong 😢')
        }
      } else {
        onCreateEnd?.({ title: inputQuery } as ArticleFlat).finally(() => (creatingDoc.current = false))
      }
    },
    [
      canCreateNewFile,
      parentFolderId,
      mentioningArticle,
      selectNewlyCreatedDoc,
      openWindowSafely,
      onCreateStart,
      onCreateEnd,
      skipCreate,
    ],
  )

  const ref = useRef<HTMLDivElement>(null)
  useKeyboardNavigation(ref, { onNavigate: resetFirstItemSelected })
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (['ArrowUp', 'ArrowDown', 'Tab', 'Shift'].includes(e.key)) return

      // On Enter, trigger the selected element or the first
      // The "focused" element is tracked only on DOM, so we need to dispatch a click event in the end
      if (e.key === 'Enter') {
        if (!ref.current) return

        let active = document.activeElement

        // the focused element is already a mention prompt option
        if (active && ref.current.contains(active)) return

        // the focused element isn't a mention prompt option
        // selects the first non disabled
        if (!active || !ref.current.contains(active)) {
          active = ref.current.querySelector('[tabindex]:not([disabled]):not([tabindex="-1"])')
        }

        // safe guard
        if (!active || typeof active.dispatchEvent !== 'function') return

        active.dispatchEvent(new MouseEvent('click', { bubbles: true }))
        return e.preventDefault()
      }

      if (e.key === 'Escape') {
        if (creatingDoc.current) return
        e.preventDefault()
        onCancel?.()
      }

      onKeyDown(e)
      setFirstItemSelected(true) // Navigate away from the prompt
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [onKeyDown, onCancel, setFirstItemSelected])

  const itemType = handbookItemType ? capitalize(stringType(handbookItemType)) : 'Doc'

  const createNewDocRow = () => {
    const tabIndex = totalResults + (extraOptions?.length || 0)

    return (
      <div
        tabIndex={tabIndex}
        onBlur={onBlur}
        key="createDoc"
        className={cx(Styles.resultRow, Styles.createDocRow, {
          [Styles.empty]: queryIsEmpty,
        })}
        onClick={() => {
          if (!queryIsEmpty) createDocFromInputQuery(inputQuery as string, auth)
        }}
        onKeyPress={(e) => {
          if (e.key === 'Enter' && !queryIsEmpty) {
            createDocFromInputQuery(inputQuery as string, auth)
            e.preventDefault()
          }
        }}
      >
        <div className={Styles.iconContainer}>
          {handbookItemType === HandbookItemType.NestedHandbook ? <NewFolder /> : <NewDoc />}
        </div>

        <div className={cx(Styles.createDocText, { [Styles.createDocTextDisabled]: queryIsEmpty })}>
          Create
          {queryIsEmpty ? (
            <span className={Styles.createDocPlaceholder}> New {itemType}</span>
          ) : (
            <span className={Styles.createDocSearchedQuery}> {originalQuery}</span>
          )}
        </div>

        {!queryIsEmpty && <div className={Styles.createDocHint}>New {itemType}</div>}
      </div>
    )
  }

  const renderArticleResult = (article: MentionableArticle) => {
    const index = renderIndex
    renderIndex += 1

    const { header_emoji, external_file_icon, objectID, title } = article

    return (
      <div
        tabIndex={index}
        onBlur={onBlur}
        key={`article-${objectID}`}
        className={cx(Styles.resultRow, { [Styles.selected]: firstItemSelected && index === 0 })}
        onClick={(e) => {
          selectArticle(article)
        }}
        onKeyPress={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            selectArticle(article)
          }
        }}
      >
        {header_emoji || external_file_icon ? (
          <Avatar
            className={Styles.emojiContainer}
            emoji={<ArticleFileIcon customClassName={Styles.fileIcon} article={article} />}
            size={16}
          />
        ) : (
          <div className={Styles.iconContainer}>
            <Doc />
          </div>
        )}
        <div className={Styles.resultRowText}>
          <span className={Styles.resultRowTitle}>{title}</span>
        </div>
      </div>
    )
  }
  const renderFolderResult = (folder: MentionableArticle) => {
    const index = renderIndex
    renderIndex += 1

    const { header_emoji, objectID, title } = folder

    return (
      <div
        tabIndex={index}
        onBlur={onBlur}
        key={`folder-${objectID}`}
        className={cx(Styles.resultRow, { [Styles.selected]: firstItemSelected && index === 0 })}
        onClick={(e) => {
          selectFolder(folder)
        }}
        onKeyPress={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            selectFolder(folder)
          }
        }}
      >
        {header_emoji ? (
          <Avatar className={Styles.emojiContainer} emoji={header_emoji} size={16} />
        ) : (
          <div className={Styles.iconContainer}>
            <FolderIcon />
          </div>
        )}
        <div className={Styles.resultRowText}>
          <span className={Styles.resultRowTitle}>{title}</span>
        </div>
      </div>
    )
  }

  const renderUserResult = (user: MentionUser) => {
    const index = renderIndex
    renderIndex += 1

    const { avatar_url, id, display_name } = user
    return (
      <div
        tabIndex={index}
        onBlur={onBlur}
        key={`user-result-${id}`}
        data-testid={`mention-user-${user.id}`}
        className={cx(Styles.resultRow, { [Styles.selected]: firstItemSelected && index === 0 })}
        onClick={(e) => {
          selectUser(user)
        }}
        onKeyPress={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            selectUser(user)
          }
        }}
      >
        <div className={Styles.avatarWrap}>
          <Avatar label={getInitials(display_name)} size={16} url={avatar_url || undefined} />
        </div>
        <div className={Styles.resultRowText}>
          <span className={Styles.resultRowTitle}>{display_name}</span>
        </div>
      </div>
    )
  }

  const renderExtraOptionResult = ([Option, args]: [any, any], i: number) => {
    const index = renderIndex
    renderIndex += 1

    return (
      <Option
        tabIndex={index}
        onBlur={onBlur}
        key={`options-${i}`}
        query={query}
        className={cx(Styles.resultRow, { [Styles.selected]: firstItemSelected && i === 0 })}
        onKeyPress={(e: KeyboardEvent) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            args.action && args.action()
          }
        }}
        {...args}
      />
    )
  }

  const renderExtraOptions = () => {
    if (!extraOptions) return []

    const Divider = () => (totalResults > 0 ? <span className={Styles.divider} /> : null)

    return (
      <>
        {extraOptionsPosition === 'bottom' && <Divider />}
        {extraOptions.map(renderExtraOptionResult)}
        {extraOptionsPosition === 'top' && <Divider />}
      </>
    )
  }

  const someAlreadyFetched = articlesAndFoldersQuery.isFetched || usersQuery.isFetched || !objectsToLoad.length

  const renderResults = () => {
    const articleData = articlesAndFoldersQuery?.data?.filter((item) => !item.objectID.includes('_'))
    const folderData = articlesAndFoldersQuery?.data?.filter((item) => item.objectID.includes('Project'))

    const doRender = () => {
      return someAlreadyFetched ? (
        <>
          {!skipLoadingUsers && usersQuery.data?.map((item: any) => renderUserResult(item))}
          {articleData?.map((item: any) => renderArticleResult(item))}
          {folderData?.map((item: any) => renderFolderResult(item))}
        </>
      ) : (
        <>
          {[...Array(DefaultItems)].map((_, i) => (
            <div key={`stub-${i}`}>
              <ResultStub index={i} />
            </div>
          ))}
        </>
      )
    }

    return (
      <>
        {extraOptionsPosition === 'top' && renderExtraOptions()}
        {doRender()}
        {extraOptionsPosition === 'bottom' && renderExtraOptions()}
      </>
    )
  }

  if (
    queryIsEmpty &&
    // skip renders if recently read or extra options aren't present
    !(suggestRecentlyReadArticle || extraOptions) &&
    !canCreateNewFile &&
    !loadOnlyUsers
  ) {
    return null
  }

  return (
    <div
      ref={ref}
      className={overrideContainerClassname || Styles.container}
      style={{ width: dropdownWidth }}
      onMouseEnter={resetFirstItemSelected}
      onMouseLeave={() => setFirstItemSelected(true)}
    >
      <div className={cx(Styles.resultsList, resultsListClassName)}>
        {(totalResults > 0 || showTitleWithNoResults) && <div className={Styles.title}>{title}</div>}
        {createNewDocPos === 'top' && canCreateNewFile && createNewDocRow()}
        {renderResults()}
        {createNewDocPos === 'bottom' && canCreateNewFile && createNewDocRow()}
      </div>
    </div>
  )
}

export const openMentionPrompt = async (view: EditorView, triggerPos: number, length: number) => {
  const anchorRes = view.state.doc.resolve(triggerPos - 1)
  const headRes = view.state.doc.resolve(anchorRes.pos + length + 1)
  const selection = new TextSelection(anchorRes, headRes)
  const tx = view.state.tr.setSelection(selection)
  tx.replaceSelectionWith(view.state.schema.text(TRIGGER_CHAR))
  view.dispatch(tx)
}
